import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, RotateCcw, Save, Lock, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const PAGE_CATALOG = [
  { key: "dashboard", label: "Dashboard" },
  { key: "orders", label: "Orders" },
  { key: "enquiries", label: "Enquiries" },
  { key: "calendar", label: "Calendar" },
  { key: "logistics", label: "Logistics" },
  { key: "driver", label: "My Trips (Driver)" },
  { key: "kitchen", label: "Kitchen Prep" },
  { key: "menu", label: "Menu" },
  { key: "inventory", label: "Inventory" },
  { key: "purchasing", label: "Purchasing" },
  { key: "warehouse", label: "Warehouse" },
  { key: "staff", label: "Staff" },
  { key: "communications", label: "Team Feed" },
  { key: "ai", label: "AI Insights" },
  { key: "cost", label: "Cost Control" },
  { key: "reports", label: "Reports" },
  { key: "users", label: "Users" },
  { key: "signups", label: "Signup Queue" },
  { key: "permissions", label: "Permissions" },
  { key: "portal", label: "Customer Portal" },
  { key: "plan", label: "Plan My Event (Customer)" },
  { key: "settings", label: "Settings" },
];

const BUILTIN_ROLES = ["admin", "operations", "kitchen", "driver", "setup", "customer"];

// admin must always retain these
const ADMIN_LOCKED = new Set(["users", "permissions", "settings"]);

export default function PermissionsPage() {
  const [perms, setPerms] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [customRoles, setCustomRoles] = useState([]);
  const [roleEditor, setRoleEditor] = useState(null); // {} = new, {id...} = edit

  const ROLES = [...BUILTIN_ROLES, ...customRoles.map((r) => r.key)];

  const load = async () => {
    const [rp, rc] = await Promise.all([
      api.get("/permissions"),
      api.get("/custom-roles").catch(() => ({ data: [] })),
    ]);
    setPerms(rp.data); setCustomRoles(rc.data || []); setDirty({});
  };
  useEffect(() => { load(); }, []);

  const toggle = (role, pageKey) => {
    if (role === "admin" && ADMIN_LOCKED.has(pageKey)) return;
    const current = dirty[role] || perms[role] || [];
    const next = current.includes(pageKey) ? current.filter((x) => x !== pageKey) : [...current, pageKey];
    setDirty({ ...dirty, [role]: next });
  };

  const save = async (role) => {
    setSaving(true);
    try {
      const pages = dirty[role];
      await api.patch("/permissions", { role, pages });
      toast.success(`${role} permissions saved`);
      await load();
      window.dispatchEvent(new Event("permissions-updated"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL role permissions to factory defaults?")) return;
    await api.post("/permissions/reset");
    toast.success("All permissions reset");
    load();
    window.dispatchEvent(new Event("permissions-updated"));
  };

  const saveCustomRole = async (r) => {
    try {
      if (r.id) await api.patch(`/custom-roles/${r.id}`, { name_en: r.name_en, name_cn: r.name_cn, color: r.color, description: r.description });
      else await api.post("/custom-roles", { name_en: r.name_en, name_cn: r.name_cn, color: r.color, description: r.description });
      toast.success(r.id ? "Role updated" : "Role created");
      setRoleEditor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const deleteCustomRole = async (r) => {
    if (!window.confirm(`Delete role "${r.name_en}"? Users holding this role must be reassigned first.`)) return;
    try { await api.delete(`/custom-roles/${r.id}`); toast.success("Role deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="permissions-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Access Control</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#D4AF37]" /> Role Permissions
          </h1>
          <p className="text-zinc-500 text-sm mt-2">Toggle which pages each role can see. Admin always retains Users, Permissions and Settings.</p>
        </div>
        <Button onClick={resetAll} variant="outline" data-testid="reset-perms" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to defaults
        </Button>
      </div>

      {/* Custom roles management */}
      <div className="card-lux p-6 mb-6" data-testid="custom-roles-card">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="label-eyebrow">Custom Roles</div>
            <p className="text-[11px] text-zinc-500 mt-1">Add extra roles beyond the built-in 6 (e.g. Cashier, Marketing). Custom roles grant sidebar permissions but don't unlock built-in workflow features.</p>
          </div>
          <Button onClick={() => setRoleEditor({ name_en: "", name_cn: "", color: "#10B981", description: "" })} data-testid="add-custom-role" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Custom Role
          </Button>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {customRoles.length === 0 && <div className="text-xs text-zinc-500 col-span-3 py-3">No custom roles yet.</div>}
          {customRoles.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]" data-testid={`custom-role-${r.key}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }}></span>
                  <span className="text-sm text-zinc-100 truncate">{r.name_en}</span>
                  {r.name_cn && <span className="text-xs text-zinc-500">· {r.name_cn}</span>}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">{r.key}</div>
              </div>
              <div className="flex gap-1">
                <Button onClick={() => setRoleEditor(r)} variant="outline" size="sm" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm h-7 w-7 p-0" data-testid={`custom-role-edit-${r.key}`}><Pencil className="w-3 h-3" /></Button>
                <Button onClick={() => deleteCustomRole(r)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm h-7 w-7 p-0" data-testid={`custom-role-delete-${r.key}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-lux overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#1a1a1a]">
            <th className="p-4 text-left label-eyebrow text-[10px] sticky left-0 bg-[#121212]">Page</th>
            {ROLES.map((r) => (
              <th key={r} className="p-4 text-center label-eyebrow text-[10px] capitalize">{r}</th>
            ))}
          </tr></thead>
          <tbody>
            {PAGE_CATALOG.map((p) => (
              <tr key={p.key} className="border-b border-[#1a1a1a]" data-testid={`perm-row-${p.key}`}>
                <td className="p-4 text-sm sticky left-0 bg-[#121212]">{p.label}</td>
                {ROLES.map((role) => {
                  const list = dirty[role] || perms[role] || [];
                  const enabled = list.includes(p.key);
                  const locked = role === "admin" && ADMIN_LOCKED.has(p.key);
                  return (
                    <td key={role} className="p-4 text-center">
                      <button
                        onClick={() => toggle(role, p.key)}
                        disabled={locked}
                        data-testid={`cell-${role}-${p.key}`}
                        className={`w-7 h-7 rounded-sm border transition-colors mx-auto flex items-center justify-center ${
                          enabled ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "bg-transparent border-[#2a2a2a] text-zinc-600 hover:border-[#D4AF37]/40"
                        } ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                        title={locked ? "Locked: admin must retain this" : enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
                      >
                        {enabled ? "✓" : locked ? <Lock className="w-3 h-3" /> : ""}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td className="p-4"></td>
              {ROLES.map((role) => (
                <td key={role} className="p-4 text-center">
                  {dirty[role] !== undefined ? (
                    <Button onClick={() => save(role)} disabled={saving} data-testid={`save-${role}`} className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] h-8 text-xs px-3">
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                  ) : (
                    <span className="text-[10px] text-zinc-600">no changes</span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {roleEditor && <CustomRoleEditor role={roleEditor} onClose={() => setRoleEditor(null)} onSave={saveCustomRole} />}
    </div>
  );
}

function CustomRoleEditor({ role, onClose, onSave }) {
  const [f, setF] = useState({
    id: role.id, name_en: role.name_en || "", name_cn: role.name_cn || "",
    color: role.color || "#10B981", description: role.description || "",
  });
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{f.id ? "Edit Custom Role" : "New Custom Role"}</DialogTitle>
          <DialogDescription className="sr-only">Create or edit a custom role for assigning to staff.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[10px]">Role Name · English</Label>
              <Input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} placeholder="e.g. Cashier" data-testid="cr-name-en" className="bg-[#050505] border-[#2a2a2a]" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[10px]">Role Name · 中文</Label>
              <Input value={f.name_cn} onChange={(e) => setF({ ...f, name_cn: e.target.value })} placeholder="例如:出纳" data-testid="cr-name-cn" className="bg-[#050505] border-[#2a2a2a]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Color (chip)</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="cr-color" className="w-12 h-10 bg-transparent border border-[#2a2a2a] rounded-sm cursor-pointer" />
              <Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="bg-[#050505] border-[#2a2a2a] font-mono" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Description (optional)</Label>
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What does this role do?" data-testid="cr-description" className="bg-[#050505] border-[#2a2a2a]" />
          </div>
          {!f.id && <p className="text-[10px] text-zinc-500">After creating, scroll down to set which pages this role can access in the permissions table.</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
            <Button onClick={() => onSave(f)} disabled={!f.name_en} data-testid="cr-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm"><Save className="w-3.5 h-3.5 mr-1.5" />{f.id ? "Save Changes" : "Create Role"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
